import { useCallback, useMemo, useState } from 'react';
import { usePowerSyncDb } from '../../../adapters/powersync';
import {
  createPatientAllergyStmt,
  restorePatientAllergyStmt,
  upsertAllergyReviewStmt,
  statusForAction,
  type AllergyClassification,
  type AllergyResolutionAction,
} from '@helios/data-access';

/* ------------------------------------------------------------------ */
/*  ExtractedAllergyReviewModal (PRD.272)                             */
/*                                                                    */
/*  Confirm-before-add review of AI-extracted allergy mentions. NOTHING */
/*  is written to patient_allergies until the clinician explicitly      */
/*  confirms here. Each item is classified (new / already recorded /    */
/*  possible duplicate / previously removed); the clinician chooses an   */
/*  action per item. Creation/restoration reuse the existing            */
/*  patient_allergies write path (additive INSERT / deleted_at = NULL). */
/*  A final duplicate re-check runs immediately before each write.       */
/* ------------------------------------------------------------------ */

export interface AllergyReviewItem {
  rawValue: string;
  normalizedValue: string;
  classification: AllergyClassification;
  matchedAllergyId?: string;
  matchedSubstance?: string;
  matchedRemoved?: boolean;
  /** Existing review row id (created up-front by the panel). */
  reviewId?: string;
  /** Current persisted status, if already resolved. */
  status?: string;
}

export interface AllergyReviewContext {
  orgId: string;
  clinicId: string | null;
  patientId: string;
  visitId: string;
  extractionId: string;
  sourceRecordingId: string | null;
  userId: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  items: AllergyReviewItem[];
  context: AllergyReviewContext;
  /** Gate create/restore actions on patient-allergy edit permission. */
  canEditAllergy: boolean;
  /** PRD.274 — source conversation date/time shown in the prompt header. */
  conversationLabel?: string;
}

type NewChoice = 'add' | 'skip';
type DupChoice = 'use_existing' | 'create_separately' | 'dismiss' | 'none';
type RemovedChoice = 'restore' | 'create_new' | 'dismiss' | 'none';

interface ItemState {
  newChoice: NewChoice;
  dupChoice: DupChoice;
  removedChoice: RemovedChoice;
  allergyType: string; // '' | 'drug' | 'food'
  severity: string;
  reaction: string;
}

interface ResultSummary {
  added: number;
  matched: number;
  restored: number;
  dismissed: number;
  failed: number;
  perItem: { name: string; label: string; ok: boolean }[];
}

export function ExtractedAllergyReviewModal({
  open,
  onClose,
  items,
  context,
  canEditAllergy,
  conversationLabel,
}: Props) {
  const { db } = usePowerSyncDb();
  const [state, setState] = useState<Record<string, ItemState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ResultSummary | null>(null);

  const get = useCallback(
    (it: AllergyReviewItem): ItemState =>
      state[it.normalizedValue] ?? {
        // New allergies preselected (PRD selection rules); everything else off.
        newChoice: it.classification === 'new' ? 'add' : 'skip',
        dupChoice: 'none',
        removedChoice: 'none',
        allergyType: '',
        severity: '',
        reaction: '',
      },
    [state],
  );

  const set = useCallback((norm: string, patch: Partial<ItemState>) => {
    setState((prev) => ({
      ...prev,
      [norm]: {
        ...(prev[norm] ?? {
          newChoice: 'skip',
          dupChoice: 'none',
          removedChoice: 'none',
          allergyType: '',
          severity: '',
          reaction: '',
        }),
        ...patch,
      },
    }));
  }, []);

  // The confirm action is enabled only when at least one item has an actionable
  // selection and the user may edit allergies.
  const hasEligible = useMemo(() => {
    if (!canEditAllergy) return false;
    return items.some((it) => {
      const s = get(it);
      if (it.classification === 'new') return s.newChoice === 'add';
      if (it.classification === 'possible_duplicate') return s.dupChoice !== 'none';
      if (it.classification === 'previously_removed') return s.removedChoice !== 'none';
      return false;
    });
  }, [items, get, canEditAllergy]);

  const persistReview = useCallback(
    async (
      it: AllergyReviewItem,
      action: AllergyResolutionAction,
      resolvedAllergyId: string | null,
      now: string,
    ) => {
      if (!db) return;
      // Idempotent upsert on a deterministic id per (extraction, mention) — safe
      // to re-run, no duplicate rows, no unique-constraint queue poisoning.
      const stmt = upsertAllergyReviewStmt({
        orgId: context.orgId,
        clinicId: context.clinicId,
        patientId: context.patientId,
        visitId: context.visitId,
        extractionId: context.extractionId,
        sourceRecordingId: context.sourceRecordingId,
        rawValue: it.rawValue,
        normalizedValue: it.normalizedValue,
        status: statusForAction(action),
        resolutionAction: action,
        resolvedAllergyId,
        userId: context.userId,
        now,
        dismissed: action === 'dismissed',
      });
      await db.execute(stmt.sql, stmt.params);
    },
    [db, context],
  );

  const confirm = useCallback(async () => {
    if (!db) return;
    setSubmitting(true);
    const summary: ResultSummary = {
      added: 0,
      matched: 0,
      restored: 0,
      dismissed: 0,
      failed: 0,
      perItem: [],
    };

    // Fresh active allergies for the final duplicate re-check (PRD Write §1).
    let freshActive: { id: string; substance: string | null }[] = [];
    try {
      freshActive = await db.getAll<{ id: string; substance: string | null }>(
        `SELECT id, substance FROM patient_allergies WHERE patient_id = ? AND deleted_at IS NULL`,
        [context.patientId],
      );
    } catch {
      /* fall through with empty list */
    }
    const exactActive = (raw: string): string | null => {
      const n = normalize(raw);
      const hit = freshActive.find((a) => normalize(a.substance) === n);
      return hit ? hit.id : null;
    };

    for (const it of items) {
      const s = get(it);
      const now = new Date().toISOString();
      try {
        // Resolve the intended action for this item.
        let action: AllergyResolutionAction | null = null;
        let create = false;
        let restoreId: string | null = null;
        let matchId: string | null = null;

        if (it.classification === 'new' && s.newChoice === 'add') {
          create = true;
          action = 'created';
        } else if (it.classification === 'possible_duplicate') {
          if (s.dupChoice === 'use_existing') {
            action = 'matched';
            matchId = it.matchedAllergyId ?? null;
          } else if (s.dupChoice === 'create_separately') {
            create = true;
            action = 'created_separately';
          } else if (s.dupChoice === 'dismiss') {
            action = 'dismissed';
          }
        } else if (it.classification === 'previously_removed') {
          if (s.removedChoice === 'restore') {
            restoreId = it.matchedAllergyId ?? null;
            action = 'restored';
          } else if (s.removedChoice === 'create_new') {
            create = true;
            action = 'created';
          } else if (s.removedChoice === 'dismiss') {
            action = 'dismissed';
          }
        }

        if (!action) continue; // no decision → leave as-is

        // Final duplicate re-check before any create.
        if (create) {
          const dup = exactActive(it.rawValue);
          if (dup) {
            // Someone created it concurrently — match instead of duplicating.
            await persistReview(it, 'matched', dup, now);
            summary.matched += 1;
            summary.perItem.push({
              name: it.rawValue,
              label: 'Matched (already existed)',
              ok: true,
            });
            continue;
          }
          const allergyId = crypto.randomUUID();
          const stmt = createPatientAllergyStmt({
            id: allergyId,
            patientId: context.patientId,
            allergyType: s.allergyType || null,
            substance: it.rawValue.trim(),
            reaction: s.reaction.trim() || null,
            severity: s.severity || null,
            userId: context.userId,
            now,
          });
          await db.execute(stmt.sql, stmt.params);
          freshActive.push({ id: allergyId, substance: it.rawValue.trim() });
          await persistReview(it, action, allergyId, now);
          summary.added += 1;
          summary.perItem.push({ name: it.rawValue, label: 'Added', ok: true });
        } else if (restoreId) {
          const stmt = restorePatientAllergyStmt({ id: restoreId, userId: context.userId, now });
          await db.execute(stmt.sql, stmt.params);
          freshActive.push({ id: restoreId, substance: it.matchedSubstance ?? it.rawValue });
          await persistReview(it, action, restoreId, now);
          summary.restored += 1;
          summary.perItem.push({ name: it.rawValue, label: 'Restored', ok: true });
        } else if (action === 'matched') {
          await persistReview(it, action, matchId, now);
          summary.matched += 1;
          summary.perItem.push({ name: it.rawValue, label: 'Matched to existing', ok: true });
        } else if (action === 'dismissed') {
          await persistReview(it, action, null, now);
          summary.dismissed += 1;
          summary.perItem.push({ name: it.rawValue, label: 'Dismissed', ok: true });
        }
      } catch {
        // Per-item failure never rolls back successful items (PRD Multi-Item).
        summary.failed += 1;
        summary.perItem.push({ name: it.rawValue, label: 'Failed', ok: false });
      }
    }

    setResult(summary);
    setSubmitting(false);
  }, [db, items, get, context, persistReview]);

  if (!open) return null;

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Review allergies mentioned">
      <div style={sheet}>
        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700 }}>
          Review allergies mentioned
        </div>
        {conversationLabel && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Conversation from {conversationLabel}
          </div>
        )}
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          AI detected possible allergy information in the recorded discussion. Confirm before adding
          anything to the patient&apos;s allergy record.
        </div>

        {!canEditAllergy && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: '#b45309' }}>
            You don&apos;t have permission to edit patient allergies — you can review but not add or
            restore.
          </div>
        )}

        {result ? (
          <ResultView result={result} />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              maxHeight: 360,
              overflow: 'auto',
            }}
          >
            {items.map((it) => (
              <ItemRow
                key={it.normalizedValue}
                item={it}
                state={get(it)}
                disabled={!canEditAllergy}
                onChange={(patch) => set(it.normalizedValue, patch)}
              />
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          {result ? (
            <button type="button" style={primaryBtn} onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button type="button" style={ghostBtn} onClick={onClose} disabled={submitting}>
                Not now
              </button>
              <button
                type="button"
                style={{ ...primaryBtn, opacity: hasEligible && !submitting ? 1 : 0.5 }}
                onClick={() => void confirm()}
                disabled={!hasEligible || submitting}
              >
                {submitting ? 'Saving…' : 'Add selected allergies'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ItemRow({
  item,
  state,
  disabled,
  onChange,
}: {
  item: AllergyReviewItem;
  state: ItemState;
  disabled: boolean;
  onChange: (patch: Partial<ItemState>) => void;
}) {
  const showCreateFields =
    (item.classification === 'new' && state.newChoice === 'add') ||
    (item.classification === 'possible_duplicate' && state.dupChoice === 'create_separately') ||
    (item.classification === 'previously_removed' && state.removedChoice === 'create_new');

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{item.rawValue}</span>
        <span style={{ fontSize: 'var(--font-size-2xs, 10px)', color: 'var(--text-muted)' }}>
          {stateLabel(item)}
        </span>
      </div>

      {item.classification === 'new' && (
        <label style={checkLabel}>
          <input
            type="checkbox"
            checked={state.newChoice === 'add'}
            disabled={disabled}
            onChange={(e) => onChange({ newChoice: e.target.checked ? 'add' : 'skip' })}
          />
          Add as new allergy
        </label>
      )}

      {item.classification === 'possible_duplicate' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <ChoicePill
            label="Use existing"
            active={state.dupChoice === 'use_existing'}
            disabled={disabled}
            onClick={() => onChange({ dupChoice: 'use_existing' })}
          />
          <ChoicePill
            label="Create separately"
            active={state.dupChoice === 'create_separately'}
            disabled={disabled}
            onClick={() => onChange({ dupChoice: 'create_separately' })}
          />
          <ChoicePill
            label="Dismiss"
            active={state.dupChoice === 'dismiss'}
            disabled={disabled}
            onClick={() => onChange({ dupChoice: 'dismiss' })}
          />
        </div>
      )}

      {item.classification === 'previously_removed' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <ChoicePill
            label="Restore"
            active={state.removedChoice === 'restore'}
            disabled={disabled}
            onClick={() => onChange({ removedChoice: 'restore' })}
          />
          <ChoicePill
            label="Create new"
            active={state.removedChoice === 'create_new'}
            disabled={disabled}
            onClick={() => onChange({ removedChoice: 'create_new' })}
          />
          <ChoicePill
            label="Dismiss"
            active={state.removedChoice === 'dismiss'}
            disabled={disabled}
            onClick={() => onChange({ removedChoice: 'dismiss' })}
          />
        </div>
      )}

      {item.classification === 'already_recorded' && (
        <span style={{ fontSize: 'var(--font-size-2xs, 10px)', color: 'var(--text-muted)' }}>
          Already on the patient&apos;s allergy list — nothing to add.
        </span>
      )}

      {showCreateFields && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <select
            value={state.allergyType}
            disabled={disabled}
            onChange={(e) => onChange({ allergyType: e.target.value })}
            style={field}
            aria-label="Allergy type"
          >
            <option value="">Type (optional)</option>
            <option value="drug">Drug</option>
            <option value="food">Food</option>
          </select>
          <select
            value={state.severity}
            disabled={disabled}
            onChange={(e) => onChange({ severity: e.target.value })}
            style={field}
            aria-label="Severity"
          >
            <option value="">Severity (optional)</option>
            <option value="Mild">Mild</option>
            <option value="Moderate">Moderate</option>
            <option value="Severe">Severe</option>
          </select>
          <input
            type="text"
            value={state.reaction}
            disabled={disabled}
            placeholder="Reaction (optional)"
            onChange={(e) => onChange({ reaction: e.target.value })}
            style={{ ...field, flex: 1, minWidth: 120 }}
            aria-label="Reaction"
          />
        </div>
      )}
    </div>
  );
}

function ChoicePill({
  label,
  active,
  disabled,
  onClick,
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 26,
        padding: '0 10px',
        borderRadius: 999,
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--border)'}`,
        background: active ? 'var(--color-primary)' : 'var(--bg-surface)',
        color: active ? '#fff' : 'var(--text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 'var(--font-size-2xs, 10px)',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );
}

function ResultView({ result }: { result: ResultSummary }) {
  const lines = [
    result.added > 0 ? `${result.added} allerg${result.added === 1 ? 'y' : 'ies'} added` : null,
    result.restored > 0 ? `${result.restored} restored` : null,
    result.matched > 0 ? `${result.matched} matched to an existing allergy` : null,
    result.dismissed > 0 ? `${result.dismissed} dismissed` : null,
    result.failed > 0 ? `${result.failed} failed` : null,
  ].filter(Boolean) as string[];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {lines.length === 0 ? (
        <Muted>No changes.</Muted>
      ) : (
        lines.map((l) => (
          <div key={l} style={{ fontSize: 'var(--font-size-xs)' }}>
            {l}
          </div>
        ))
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
        {result.perItem.map((p, i) => (
          <div
            key={`${p.name}-${i}`}
            style={{
              fontSize: 'var(--font-size-2xs, 10px)',
              color: p.ok ? 'var(--text-muted)' : '#dc2626',
            }}
          >
            {p.name}: {p.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{children}</div>
  );
}

function stateLabel(it: AllergyReviewItem): string {
  switch (it.classification) {
    case 'new':
      return 'New allergy';
    case 'already_recorded':
      return 'Already recorded';
    case 'possible_duplicate':
      return it.matchedSubstance
        ? `Possible duplicate: ${it.matchedSubstance}`
        : 'Possible duplicate';
    case 'previously_removed':
      return it.matchedSubstance
        ? `Previously removed: ${it.matchedSubstance}`
        : 'Previously removed';
  }
}

/** Same normalization as allergy-check.normalizeName (kept local to avoid a data-access UI import churn). */
function normalize(v: string | null | undefined): string {
  return (v ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 16,
};
const sheet: React.CSSProperties = {
  width: 'min(560px, 96vw)',
  background: 'var(--bg-surface)',
  borderRadius: 'var(--radius-md, 10px)',
  padding: 'var(--sp-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};
const primaryBtn: React.CSSProperties = {
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--color-primary)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};
const ghostBtn: React.CSSProperties = {
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};
const checkLabel: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 'var(--font-size-xs)',
};
const field: React.CSSProperties = {
  height: 28,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-2xs, 10px)',
  padding: '0 6px',
};
